import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Lock, Loader2, CheckCircle } from 'lucide-react';
import { useSectionTranslation } from '@/hooks';
import { api, isApiError } from '@/lib/api';
import { AuthLayout } from '../components/AuthLayout';
import { resetPasswordTranslations } from './translations';

const schema = z.object({
  newPassword: z.string().min(8).max(128).regex(/[a-z]/, 'Lowercase').regex(/[A-Z]/, 'Uppercase').regex(/[0-9]/, 'Number'),
  confirmPassword: z.string(),
}).refine((d) => d.newPassword === d.confirmPassword, { message: 'Passwords do not match', path: ['confirmPassword'] });

type FormValues = z.infer<typeof schema>;

export function ResetPassword() {
  const t = useSectionTranslation(resetPasswordTranslations);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit(async (values) => {
    setLoading(true);
    setError('');
    const res = await api.post('/auth/reset-password', { token, ...values });
    setLoading(false);
    if (isApiError(res)) {
      setError(res.error.message || t.error.generic);
    } else {
      setDone(true);
      setTimeout(() => navigate('/auth/signin'), 3000);
    }
  });

  if (!token) {
    return (
      <AuthLayout title={t.page}>
        <div className="rounded-xl bg-surface-card border border-surface-border p-6 text-center space-y-4">
          <p className="text-sm text-brand-danger">{t.error.invalidToken}</p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t.page}>
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-display-md text-brand-white mb-2">{t.heading}</h1>
          <p className="text-body-sm text-brand-muted">{t.subtext}</p>
        </div>

        {done ? (
          <div className="rounded-xl bg-surface-card border border-surface-border p-6 text-center space-y-4">
            <div className="flex justify-center"><CheckCircle size={40} className="text-brand-success" /></div>
            <p className="text-sm text-brand-white">{t.success}</p>
          </div>
        ) : (
          <form onSubmit={onSubmit} noValidate className="space-y-4">
            {error && <div className="rounded-lg border border-brand-danger/30 bg-brand-danger/10 px-3 py-2.5 text-sm text-brand-danger">{error}</div>}
            <div>
              <label className="text-xs font-medium text-brand-muted block mb-1.5">{t.newPassword}</label>
              <div className="flex items-center rounded-lg bg-surface-card border border-surface-border focus-within:border-accent/30">
                <Lock size={14} className="ms-3 text-brand-muted" />
                <input {...register('newPassword')} type="password" placeholder={t.newPasswordPlaceholder} className="flex-1 bg-transparent px-3 py-2.5 text-sm text-brand-white placeholder:text-brand-muted/60 focus:outline-none" />
              </div>
              {errors.newPassword && <p className="text-xs text-brand-danger mt-1">{errors.newPassword.message}</p>}
            </div>
            <div>
              <label className="text-xs font-medium text-brand-muted block mb-1.5">{t.confirmPassword}</label>
              <div className="flex items-center rounded-lg bg-surface-card border border-surface-border focus-within:border-accent/30">
                <Lock size={14} className="ms-3 text-brand-muted" />
                <input {...register('confirmPassword')} type="password" placeholder={t.confirmPasswordPlaceholder} className="flex-1 bg-transparent px-3 py-2.5 text-sm text-brand-white placeholder:text-brand-muted/60 focus:outline-none" />
              </div>
              {errors.confirmPassword && <p className="text-xs text-brand-danger mt-1">{errors.confirmPassword.message}</p>}
            </div>
            <button type="submit" disabled={loading} className="btn-primary h-11 w-full disabled:opacity-60">
              {loading ? <><Loader2 size={16} className="animate-spin" />{t.submitting}</> : <><Lock size={16} />{t.submit}</>}
            </button>
          </form>
        )}
      </div>
    </AuthLayout>
  );
}